// Redirects y Headers (ficha 05, Hosting): tablas editables que se guardan juntas.
import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { api, type HeaderRule, type Redirect } from '../../api.ts';
import { t } from '../../i18n/index.ts';
import { errorText, useApp } from '../../store.ts';
import type { SiteProps } from './SiteShell.tsx';

export function Rules({ site, onChange, kind = 'redirects' }: SiteProps) {
  const notify = useApp((s) => s.notify);
  const [redirects, setRedirects] = useState<Redirect[]>(site.settings.redirects);
  const [headers, setHeaders] = useState<HeaderRule[]>(site.settings.headers);
  const dirty = JSON.stringify(redirects) !== JSON.stringify(site.settings.redirects) || JSON.stringify(headers) !== JSON.stringify(site.settings.headers);

  const save = async () => {
    try {
      onChange((await api.updateSite(site.id, { settings: { redirects, headers } })).site);
      notify(t('common.saved'));
    } catch (e) {
      notify(errorText(e), true);
    }
  };

  const setR = (i: number, patch: Partial<Redirect>) => setRedirects((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const setH = (i: number, patch: Partial<HeaderRule>) => setHeaders((hs) => hs.map((h, j) => (j === i ? { ...h, ...patch } : h)));

  return (
    <section className="section">
      <h2>{kind === 'redirects' ? t('site.redirects') : t('site.headers')}</h2>
      <p className="hint">{kind === 'redirects' ? t('rules.redirectsHint') : t('rules.headersHint')}</p>
      <div className="panel" style={{ overflowX: 'auto' }}>
        {kind === 'redirects' ? (
          <table className="table">
            <colgroup>
              <col />
              <col />
              <col style={{ width: 84 }} />
              <col style={{ width: 38 }} />
            </colgroup>
            <thead>
              <tr>
                <th>{t('rules.from')}</th>
                <th>{t('rules.to')}</th>
                <th>{t('rules.status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {redirects.map((r, i) => (
                <tr key={i}>
                  <td>
                    <input className="input mono" value={r.from} onChange={(e) => setR(i, { from: e.target.value })} placeholder="/old-page" />
                  </td>
                  <td>
                    <input className="input mono" value={r.to} onChange={(e) => setR(i, { to: e.target.value })} placeholder="/new-page" />
                  </td>
                  <td>
                    <select className="input" value={r.status} onChange={(e) => setR(i, { status: Number(e.target.value) as Redirect['status'] })}>
                      {[301, 302, 307, 308].map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button type="button" className="btn icon" title={t('common.remove')} onClick={() => setRedirects((rs) => rs.filter((_, j) => j !== i))}>
                      <X size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="table">
            <colgroup>
              <col style={{ width: '30%' }} />
              <col style={{ width: '30%' }} />
              <col />
              <col style={{ width: 38 }} />
            </colgroup>
            <thead>
              <tr>
                <th>{t('rules.path')}</th>
                <th>{t('rules.name')}</th>
                <th>{t('rules.value')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {headers.map((h, i) => (
                <tr key={i}>
                  <td>
                    <input className="input mono" value={h.path} onChange={(e) => setH(i, { path: e.target.value })} placeholder="/*" />
                  </td>
                  <td>
                    <input className="input mono" value={h.name} onChange={(e) => setH(i, { name: e.target.value })} placeholder="X-Frame-Options" />
                  </td>
                  <td>
                    <input className="input mono" value={h.value} onChange={(e) => setH(i, { value: e.target.value })} placeholder="DENY" />
                  </td>
                  <td>
                    <button type="button" className="btn icon" title={t('common.remove')} onClick={() => setHeaders((hs) => hs.filter((_, j) => j !== i))}>
                      <X size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <button
            type="button"
            className="btn ghost"
            onClick={() =>
              kind === 'redirects'
                ? setRedirects((rs) => [...rs, { from: '/', to: '/', status: 301 }])
                : setHeaders((hs) => [...hs, { path: '/*', name: '', value: '' }])
            }
          >
            <Plus size={12} />
            {kind === 'redirects' ? t('rules.addRedirect') : t('rules.addHeader')}
          </button>
          <button type="button" className="btn primary" disabled={!dirty} onClick={save}>
            {t('common.save')}
          </button>
        </div>
      </div>
    </section>
  );
}
